import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TableContainer,
  Alert,
  Skeleton,
  Stack,
  LinearProgress,
  Tooltip,
  Grid,
  Divider,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  MailOutline as MailIcon,
  WhatsApp as WhatsAppIcon,
  OpenInNew as OpenIcon,
  TouchApp as ClickIcon,
  Send as SentIcon,
  ErrorOutline as FailedIcon,
} from "@mui/icons-material";
import api from "../../axiosConfig.js";

// ── helpers ──────────────────────────────────────────────────────────────────

const StatusChip = ({ status }) => {
  const s = (status || "").toLowerCase();
  let color = "default";
  if (s === "done" || s === "completed") color = "success";
  else if (s === "sending" || s === "running") color = "info";
  else if (s === "scheduled") color = "warning";
  else if (s === "failed" || s === "cancelled") color = "error";
  else if (s === "draft") color = "default";
  return <Chip label={status || "—"} color={color} size="small" />;
};

const ChannelBadge = ({ channel }) => (
  <Chip
    size="small"
    icon={channel === "whatsapp" ? <WhatsAppIcon style={{ fontSize: 13 }} /> : <MailIcon style={{ fontSize: 13 }} />}
    label={channel === "whatsapp" ? "WhatsApp" : "Email"}
    sx={{
      bgcolor: channel === "whatsapp" ? "#e8f5e9" : "#e3f2fd",
      color: channel === "whatsapp" ? "#2e7d32" : "#1565c0",
      fontWeight: 600,
      fontSize: 11,
      "& .MuiChip-icon": { color: "inherit" },
    }}
    variant="filled"
  />
);

const RateBar = ({ count, total, color, label }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <Tooltip title={`${label}: ${count.toLocaleString()} / ${total.toLocaleString()} (${pct}%)`}>
      <Box sx={{ minWidth: 90 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
            {label}
          </Typography>
          <Typography variant="caption" fontWeight={700} sx={{ fontSize: 10 }}>
            {pct}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            height: 5,
            borderRadius: 3,
            bgcolor: "rgba(0,0,0,0.06)",
            "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 3 },
          }}
        />
      </Box>
    </Tooltip>
  );
};

const SummaryCard = ({ icon, label, value, color, bg }) => (
  <Paper
    sx={{
      p: 2,
      borderRadius: 2,
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      border: "1px solid rgba(0,0,0,0.07)",
      boxShadow: "none",
    }}
  >
    <Box
      sx={{
        width: 38,
        height: 38,
        borderRadius: "50%",
        bgcolor: bg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {React.cloneElement(icon, { sx: { fontSize: 20, color } })}
    </Box>
    <Box>
      <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.1 }}>
        {typeof value === "number" ? value.toLocaleString() : value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  </Paper>
);

const getEmailSent = (c) => {
  try {
    const t = typeof c.totals_json === "string" ? JSON.parse(c.totals_json) : (c.totals_json || {});
    return t.sent ?? 0;
  } catch {
    return 0;
  }
};

// ── component ─────────────────────────────────────────────────────────────────

export default function CampaignsWidget() {
  const [emailCampaigns, setEmailCampaigns] = useState([]);
  const [waCampaigns, setWaCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [emailRes, waRes] = await Promise.all([
        api.get("/email/campaigns"),
        api.get("/campaigns?page=1"),
      ]);

      setEmailCampaigns(Array.isArray(emailRes.data) ? emailRes.data : []);

      const waData = waRes.data;
      const waList = Array.isArray(waData)
        ? waData
        : Array.isArray(waData?.data)
        ? waData.data
        : [];
      setWaCampaigns(waList.slice(0, 10));
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Could not load campaigns.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ── combined summary stats ──────────────────────────────────────────────────

  const totalEmailSent = emailCampaigns.reduce((acc, c) => acc + getEmailSent(c), 0);
  const totalEmailOpened = emailCampaigns.reduce((acc, c) => acc + (c.opened_count ?? 0), 0);
  const totalEmailClicked = emailCampaigns.reduce((acc, c) => acc + (c.clicked_count ?? 0), 0);

  const totalWaSent = waCampaigns.reduce((acc, c) => acc + (c.sent_count ?? 0), 0);
  const totalWaFailed = waCampaigns.reduce((acc, c) => acc + (c.failed_count ?? 0), 0);

  const totalSent = totalEmailSent + totalWaSent;

  // ── unified row list (newest first) ────────────────────────────────────────

  const emailRows = emailCampaigns.map((c) => ({
    id: `email-${c.id}`,
    channel: "email",
    name: c.subject || "Untitled",
    status: c.status,
    created_at: c.created_at,
    sent: getEmailSent(c) || (c.recipients_count ?? 0),
    opened: c.opened_count ?? 0,
    clicked: c.clicked_count ?? 0,
    failed: 0,
    delivered: 0,
    by: c.created_by?.name || null,
  }));

  const waRows = waCampaigns.map((c) => ({
    id: `wa-${c.id}`,
    channel: "whatsapp",
    name: c.name || c.subject || "Untitled",
    status: c.status,
    created_at: c.created_at,
    sent: c.sent_count ?? 0,
    opened: 0,
    clicked: 0,
    failed: c.failed_count ?? 0,
    delivered: 0,
    by: null,
  }));

  const allRows = [...emailRows, ...waRows].sort(
    (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
  );

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <Paper sx={{ p: 3, borderRadius: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
        <Typography variant="h6" fontWeight={700} sx={{ flexGrow: 1 }}>
          Broadcast Performance
        </Typography>
        <IconButton onClick={fetchAll} size="small" disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {loading ? (
        <Stack spacing={2}>
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map((i) => (
              <Grid item xs={6} sm={3} key={i}>
                <Skeleton variant="rectangular" height={72} sx={{ borderRadius: 2 }} />
              </Grid>
            ))}
          </Grid>
          <Skeleton variant="rectangular" height={36} sx={{ borderRadius: 1 }} />
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={52} sx={{ borderRadius: 1 }} />
          ))}
        </Stack>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          {/* Summary cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <SummaryCard
                icon={<SentIcon />}
                label="Total Sent"
                value={totalSent}
                color="#16a34a"
                bg="#dcfce7"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <SummaryCard
                icon={<OpenIcon />}
                label="Email Opens"
                value={totalEmailOpened}
                color="#1565c0"
                bg="#e3f2fd"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <SummaryCard
                icon={<ClickIcon />}
                label="Email Interactions"
                value={totalEmailClicked}
                color="#6a1b9a"
                bg="#f3e5f5"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <SummaryCard
                icon={<FailedIcon />}
                label="WA Failed"
                value={totalWaFailed}
                color="#c62828"
                bg="#ffebee"
              />
            </Grid>
          </Grid>

          {/* Channel breakdown bars */}
          {(totalEmailSent > 0 || totalWaSent > 0) && (
            <Box
              sx={{
                display: "flex",
                gap: 3,
                p: 2,
                mb: 2.5,
                bgcolor: "#f9fafb",
                borderRadius: 2,
                flexWrap: "wrap",
              }}
            >
              {totalEmailSent > 0 && (
                <Box sx={{ flex: 1, minWidth: 160 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
                    <MailIcon sx={{ fontSize: 15, color: "#1565c0" }} />
                    <Typography variant="caption" fontWeight={600} color="#1565c0">
                      Email — {totalEmailSent.toLocaleString()} sent
                    </Typography>
                  </Box>
                  <Stack spacing={0.75}>
                    <RateBar count={totalEmailOpened} total={totalEmailSent} color="#16a34a" label="Open rate" />
                    <RateBar count={totalEmailClicked} total={totalEmailSent} color="#0ea5e9" label="Interaction rate" />
                  </Stack>
                </Box>
              )}
              {totalEmailSent > 0 && totalWaSent > 0 && (
                <Divider orientation="vertical" flexItem />
              )}
              {totalWaSent > 0 && (
                <Box sx={{ flex: 1, minWidth: 160 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
                    <WhatsAppIcon sx={{ fontSize: 15, color: "#2e7d32" }} />
                    <Typography variant="caption" fontWeight={600} color="#2e7d32">
                      WhatsApp — {totalWaSent.toLocaleString()} sent
                    </Typography>
                  </Box>
                  <Stack spacing={0.75}>
                    <RateBar
                      count={totalWaSent}
                      total={totalWaSent + totalWaFailed}
                      color="#25D366"
                      label="Delivery rate"
                    />
                    <RateBar
                      count={totalWaFailed}
                      total={totalWaSent + totalWaFailed}
                      color="#ef5350"
                      label="Failure rate"
                    />
                  </Stack>
                </Box>
              )}
            </Box>
          )}

          {/* Campaigns table */}
          {allRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
              No campaigns yet.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Campaign</TableCell>
                    <TableCell>Channel</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Sent</TableCell>
                    <TableCell>Performance</TableCell>
                    <TableCell>By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allRows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500} sx={{ maxWidth: 220 }} noWrap>
                          {row.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.created_at ? new Date(row.created_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" }) : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <ChannelBadge channel={row.channel} />
                      </TableCell>
                      <TableCell>
                        <StatusChip status={row.status} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {row.sent > 0 ? row.sent.toLocaleString() : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 150 }}>
                        {row.channel === "email" && row.sent > 0 ? (
                          <Stack spacing={0.5}>
                            <RateBar count={row.opened} total={row.sent} color="#16a34a" label="Opens" />
                            <RateBar count={row.clicked} total={row.sent} color="#0ea5e9" label="Interactions" />
                          </Stack>
                        ) : row.channel === "whatsapp" && (row.sent + row.failed) > 0 ? (
                          <RateBar
                            count={row.sent}
                            total={row.sent + row.failed}
                            color="#25D366"
                            label="Delivered"
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {row.by || "—"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Paper>
  );
}
